import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { CheckCircle2, Truck } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { ActionButton } from '@/shared/ui/action-button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import {
  RELEASE_ATTACHMENT_TYPES,
  type ReceiveConfirmArrivalResultDto,
  type ReceiveReleaseContextDto,
  type ReceiveReleaseResultDto
} from '@/entities/receive';
import { ConfirmArrivalDocuments } from '@/features/receive-confirm-arrival';

import { RELEASE_GOODS_FORM_ID } from '../model/constants';
import { useReleaseGoodsForm } from '../model/use-release-goods-form';
import { ReleaseGoodsHeaderFields } from './release-goods-header-fields';
import { ReleaseGoodsLinesTable } from './release-goods-lines-table';
import { ReleaseGoodsResult } from './release-goods-result';

export interface ReleaseGoodsModalProps {
  /** The receipt *Receive and Release* has just booked — its GRN numbers stay on show. */
  receipt: ReceiveConfirmArrivalResultDto;
  context: ReceiveReleaseContextDto;
  onClose: () => void;
  onSuccess?: (result: ReceiveReleaseResultDto) => void;
}

/**
 * *Release Details* — the second half of Receive and Release (legacy
 * `releaseDetailsPopup`): who the received goods were handed over to, and how
 * much of each line left.
 */
export function ReleaseGoodsModal({ receipt, context: initialContext, onClose, onSuccess }: ReleaseGoodsModalProps) {
  const { t } = useTranslation();
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);

  const { form, context, attachments, result, maxReleaseDate, isSubmitting, handleResetQuantities, handleSubmit } =
    useReleaseGoodsForm({ context: initialContext, onSuccess });

  // The receipt is already booked and nothing brings this form back, so closing
  // before the release is worth one question.
  const requestClose = () => (result ? onClose() : setIsCloseConfirmOpen(true));

  const renderBody = () => {
    if (result) {
      return <ReleaseGoodsResult result={result} />;
    }

    return (
      <form
        id={RELEASE_GOODS_FORM_ID}
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void handleSubmit(event);
        }}
        className="space-y-6">
        <Alert variant="success">
          <CheckCircle2 />
          <AlertTitle>{t('receive.confirm.result.title', 'Receipt recorded')}</AlertTitle>
          {receipt.goodsReceivedNoteNumbers.length > 0 && (
            <AlertDescription>
              {t('grn.number.label', 'Goods Received Note Number')}: {receipt.goodsReceivedNoteNumbers.join(', ')}
            </AlertDescription>
          )}
        </Alert>

        <ReleaseGoodsHeaderFields form={form} context={context} maxReleaseDate={maxReleaseDate} />

        <ReleaseGoodsLinesTable
          control={form.control}
          setValue={form.setValue}
          lines={context.releasableLines}
          onResetQuantities={handleResetQuantities}
        />

        <ConfirmArrivalDocuments
          attachments={[]}
          allowedTypes={context.allowedAttachmentTypes ?? RELEASE_ATTACHMENT_TYPES}
          pending={attachments.pending}
          comment={attachments.comment}
          onAddFiles={attachments.addFiles}
          onTypeChange={attachments.setAttachmentType}
          onRemove={attachments.removeFile}
          onCommentChange={attachments.setComment}
          pendingTitle={t('receive.release.attachments.toUpload', 'Will be attached to this release')}
        />

        <div className="flex items-center gap-2">
          <Controller
            name="printGoodsReleaseNote"
            control={form.control}
            render={({ field }) => (
              <Checkbox id="printGoodsReleaseNote" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
          <Label htmlFor="printGoodsReleaseNote" className="text-sm font-normal">
            {t('dispatch.print.goods.release.note.button', 'Print Goods Release Note')}
          </Label>
        </div>
      </form>
    );
  };

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && requestClose()}>
        <DialogContent
          // Same sizing as the receipt modal — see the note there on `sm:max-w-…`.
          className="grid max-h-[92vh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden sm:max-w-[min(1200px,calc(100%-2rem))]"
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-muted-foreground" aria-hidden />
              {t('dispatch.release.multi.popup.title', 'Release Details')}
            </DialogTitle>
            <DialogDescription>
              {t('receive.release.description', 'Record who the received goods were handed over to.')}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 overflow-y-auto overflow-x-hidden">{renderBody()}</div>

          <DialogFooter className="gap-2 border-t pt-4 sm:justify-center">
            <ActionButton type="button" intent="dismiss" className="min-w-[140px]" onClick={requestClose}>
              {t('gen.closedialog.label', 'Close Dialog')}
            </ActionButton>
            {!result && (
              <ActionButton
                intent="primary"
                type="submit"
                form={RELEASE_GOODS_FORM_ID}
                className="min-w-[140px]"
                disabled={isSubmitting}>
                {isSubmitting ? <Spinner className="mr-2 h-4 w-4" /> : <Truck className="mr-2 h-4 w-4" />}
                {t('dispatch.release.button', 'Release Goods')}
              </ActionButton>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isCloseConfirmOpen} onOpenChange={setIsCloseConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('receive.release.closeConfirm.title', 'Close without releasing?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'receive.release.closeConfirm.description',
                'The goods have been received but not released. This release cannot be reopened from the receive dashboard.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('gen.cancel.label', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onClose}>{t('gen.closedialog.label', 'Close Dialog')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
